import { useEffect, useMemo } from "react";
import type { FieldPath } from "react-hook-form";

import type { LPQuoteInput } from "../../types/Rater.types";
import { DeductibleKey, getAllowedDeductibleKeys, getDeductibleOptions } from "../rules";
import type { BusinessRulesArgs } from "./useBusinessRules";

export function useDeductibleRules({ locationValues, tabItem, setValue, enabled }: BusinessRulesArgs) {
  const { deductible } = locationValues;
  const normalizedDeductible = String(deductible) as DeductibleKey;

  const allowedDeductibleKeys = useMemo(() => getAllowedDeductibleKeys(enabled), [enabled]);

  const deductibleOptions = useMemo(() => getDeductibleOptions(enabled), [enabled]);

  useEffect(() => {
    if (!deductible) return;

    const deductiblePath = `locations.${tabItem}.deductible` as FieldPath<LPQuoteInput>;
    const isAllowed = allowedDeductibleKeys.includes(normalizedDeductible as DeductibleKey);

    if (isAllowed) {
      if (typeof deductible !== "string") {
        setValue(deductiblePath, normalizedDeductible, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
      return;
    }

    const fallbackDeductible = allowedDeductibleKeys[0];
    if (!fallbackDeductible) return;

    setValue(deductiblePath, fallbackDeductible, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [deductible, normalizedDeductible, allowedDeductibleKeys, setValue, tabItem]);

  return { deductibleOptions, allowedDeductibleKeys };
}
